import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dateFormat from 'dateformat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useQuery } from '../../utils/hooks';
import classes from './myProfile.module.scss';
import { addNewWork, deleteExistingWork, fetchUserData, getProfilePercentage, manageConsent, saveContractType, saveJobLocation, savePersonalData, saveUserRates, setFunctions, setIndustries, switchJobSeeking, updateExistingWork, updateProfileSummary } from '../../actions/user';
import ProfileHeader from './Components/Header/ProfileHeader';
import AboutComponent from './Components/About/AboutComponent';
import ResumeSection from './Components/ResumeSection/ResumeSection';
import EditPersonalDetails from '../../Components/pages/myProfile/components/PersonalDetails/EditPersonalDetails/EditPersonalDetails';
import AddEditFormModal from '../../Components/common/ModalWindows/AddEditFormModal';
import DeleteDialog from '../../Components/common/ModalWindows/DeleteDialog';
import { appNotificationsTypes, deleteModalConfigs, maxMoneyValue, validationMessages } from '../../utils/constants';
import { addEducationsItem, addTrainingsItem, addWorkItem, deleteEducationsItem, deleteTrainingsItem, deleteWorkItem, getEducations, getSkills, getTrainings, getWorksHistory, updateEducationsItem, updateSkillsItems, updateTrainingsItem, updateWorkItem } from '../../actions/cv';
import MyProfileSkeleton from './MyProfileSkeleton';
import dayjs from 'dayjs';
import { generalEnums } from '../../utils/enums';
import { removeNotification, setNotification } from '../../actions/appNotifications';
import BaseModal from '../../Components/common/ModalWindows/BaseModal';
import PreferenceTypeThree from '../../Components/Form/Preference/PreferenceTypeThree/PreferenceTypeThree';
import { DeleteOutlined, InfoOutlined, ModeOutlined, NewspaperOutlined, SchoolOutlined, LockOutlined, NextPlanOutlined, UploadFileOutlined } from '@mui/icons-material';
import { JobPreferences } from '../../Components/pages/myProfile/components/JobPreferences/JobPreferences';
import { useFormik } from 'formik';
import { getInitialValues, getValidationSchema } from '../../Components/common/ModalWindows/helpers/addEditForm';
import ConsentForm from '../../Components/pages/myProfile/components/ConsentForm/ConsentForm';
import colors from '../../scss/colors';
import { Drawer, useMediaQuery } from '@mui/material';
import CVUploader from '../../Components/common/CVUploader/CVUploader';
import MainButton from '../../Components/common/buttons/MainButton';
import { buttonVariants } from '../../Components/common/buttons/types';
import ProfileBuilder from './Components/ProfileBuilder/ProfileBuilder';
import BuildingProgress from './Components/ProfileBuilder/BuildingProgress';
import { acknowledgeCvParse, cvParse, cvUpload } from '../../actions/uploadData';
import { get, set } from 'lodash';
import { cvParser } from '../../utils/dataParsers';
import api from '../../api/apiSettings';
import ProfileStrength from './Components/ProfileStrength/ProfileStrength';
import { fetchAppliedJobs } from '../../actions/jobs';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseSwipeableDrawer from '../../Components/common/BaseSwipeableDrawer/BaseSwipeableDrawer';
import { extractCountryCode } from '../../utils/helpers';

function MyProfile() {
  dayjs.extend(advancedFormat);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const locationRoute = useLocation();
  const userId = localStorage.getItem('UserId');
  const query = useQuery();
  const dispatch = useDispatch();
  const [selectedCV, setSelectedCV] = useState([]);
  const [isCVExtracting, setIsCVExtracting] = useState(false);
  const [editSummary, setEditSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [assessedProfile, setAssessedProfile] = useState([]);
  const [showPercentageMobile, setShowPercentageMobile] = useState(false);
  const { t } = useTranslation();
  const profile = useSelector((state) => state?.user?.profile);
  const profileError = useSelector((state) => state.user.error);
  const profilePending = useSelector((state) => state.user.pendingFlag);
  const updatingError = useSelector((state) => state.cv.updatingError);
  const updatingPending = useSelector((state) => state.cv.updatingPending);
  const updatingUserPending = useSelector((state) => state.user.updatingPending);
  const updateSkillsPending = useSelector((state) => state.cv?.skills?.pendingFlag);
  const userRates = useSelector((state) => state.user.profile?.UserRates[0]);
  const appliedJobs = useSelector((state) => state?.jobs?.response);
  const workHistory = useSelector((state) => state?.cv?.worksHistory?.response || {});
  const cv = useSelector((state) => state?.cv || {});
  const { pendingParse, pendingUpload, parsedCV, isCvParsed } = useSelector((state) => state?.uploadData || {});

  const userData = useSelector((state) => state?.user?.profile);
  const percentages = useSelector((state) => state?.user?.profileCompletion);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);


  const [editJobPreferences, setEditJobPreferences] = useState(false);
  const [editConsent, setEditConsent] = useState(false);
  const [editPersonalDetails, setEditPersonalDetails] = useState(false);
  const [deleteWorkExperience, setDeleteWorkExperience] = useState('');
  const [consent, setConsent] = useState(profile?.IsManageConcent);
  const [userRatesLocaly, setUserRatesLocaly] = useState(null);
  const initialErrorsState = {
    hourly: '',
    daily: '',
    annual: ''
  };
  const [validationErrors, setValidationErrors] = useState(initialErrorsState);

  const deleteModalConfig = useMemo(() => deleteModalConfigs(t), [t]);

  const [modalConfig, setModalConfig] = useState({
    section: '',
    item: null,
    mode: null
  });

  const openAddModal = (section) =>
    setModalConfig({ section, item: null, mode: 'add' });

  const openEditModal = (section, item) =>
    setModalConfig({ section, item, mode: 'edit' });

  const openDeleteModal = (section, item) =>
    setModalConfig({ section, item, mode: 'delete' });

  const [editWorkLocation, setEditWorkLocation] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUserData(userId));
    dispatch(getProfilePercentage());
  }, []);

  useEffect(() => {
    if (userData) {
      setIsProfileCompleted(userData.IsProfileCompleted);
    }
  }, [userData?.IsProfileCompleted]);

  const validateForm = (values) => {
    let initialValidation = {
      hourly: '',
      daily: '',
      annual: ''
    };

    if (Number(values.Hourly) > maxMoneyValue)
      initialValidation = { ...initialValidation, hourly: validationMessages(t).MAX_MONEY_VALUE };
    if (Number(values.Daily) > maxMoneyValue)
      initialValidation = { ...initialValidation, daily: validationMessages(t).MAX_MONEY_VALUE };
    if (Number(values.Annual) > maxMoneyValue)
      initialValidation = { ...initialValidation, annual: validationMessages(t).MAX_MONEY_VALUE };

    setValidationErrors(initialValidation);

    return !Object.values(initialValidation).some((value) => !!value);
};

const confirmTerms = () => {
  const isFormValid = validateForm(userRatesLocaly);
  if (isFormValid) {
    updateUserRates(userRatesLocaly);
  }
};

  const currentWork = profile?.WorkHistories
    .filter((job) => job.CurrentlyWorkThere)
    ?.sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate))[0];

  const personalData = {
    SecondaryEmail: profile?.SecondaryEmail,
    Phone: profile?.Phone,
    PhoneCountryCode: profile?.PhoneCountryCode,
    PhoneType: profile?.PhoneType,
    City: profile?.City,
    State: profile?.State,
    Country: profile?.Country,
    AddressLine1: profile?.AddressLine1,
    AddressLine2: profile?.AddressLine2,
    ZipCode: profile?.ZipCode
  };

  const [location, setLocation] = useState({
    OpentoRemoteWork: profile?.OpentoRemoteWork,
    OpenToRelocation: profile?.OpenToRelocation
  });
  const [contractType, setContractType] = useState({
    ContractWork: profile?.ContractWork,
    PermanentWork: profile?.PermanentWork,
  });

  const [isJobPreferencesSaving, setIsJobPreferencesSaving] = useState(false);

  const isNegativeOffset = new Date().getTimezoneOffset() > 0;

  const updatePersonalData = async (data) => {
    await dispatch(savePersonalData(userId, { ...personalData, ...data }));
    await setEditPersonalDetails(false);
    await dispatch(getProfilePercentage());
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Personal details updated successfully',
      duration: 3000
    }));
  };

  const updateWork = async (work) => {
    try {
      const start = new Date(work.startdate);
      start.setHours(0);
      const end = new Date(work.enddate);
      end.setHours(0);

      const updatedWork = {
        CompanyName: work.company,
        Title: work.title,
        Description: work.description,
        CurrentlyWorkThere: work?.currentlyWorkThere || false,
        StartDate: dateFormat(start.toString() || '', isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        EndDate: dateFormat(end.toString() || '', isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        Id: work.id
      };

      await dispatch(updateWorkItem(updatedWork));
      await dispatch(fetchUserData(userId));
      // await dispatch(updateExistingWork(updatedWork));
      await dispatch(getProfilePercentage());
      await setModalConfig({
        section: '',
        item: null,
        mode: null
      });
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Work experience updated successfully',
        duration: 3000
      }));
    } catch (error) {
      console.log('Error updating work experience:', error.response);
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while updating work experience',
        duration: 3000
      }));
    }
  };

  const addWork = async (work) => {
    try {
    const start = new Date(work.startdate);
    start.setHours(0);
    const end = new Date(work.enddate);
    end.setHours(0);

    const newWork = {
      CompanyName: work.company,
      Title: work.title,
      Description: work.description,
      CurrentlyWorkThere: work?.currentlyWorkThere || false,
      StartDate: work.startdate ? dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate') : dayjs(),
      EndDate: work.enddate ? dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate') : dayjs()
    };

    await dispatch(addWorkItem(newWork));
    await dispatch(addNewWork(newWork));
    await dispatch(fetchUserData(userId));
    await dispatch(getProfilePercentage());
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Work experience added successfully',
      duration: 3000
    }));
    } catch (error) {
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while adding work experience',
        duration: 3000
      }));
    }
  };

  const addEducation = async (education) => {
    try {
      const start = education.startdate ? new Date(education.startdate) : new Date();
      start.setHours(0);
      const end = education.enddate ? new Date(education.enddate) : new Date()
      end.setHours(0);

      await dispatch(
        addEducationsItem({
          UniversityName: education.school,
          Degree: education.degree,
          DegreeType: education.degreetype,
          FieldOfStudy: education.fieldofstudy,
          City: 'null',
          State: 'null',
          Country: 'null',
          StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
          EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
          CurrentlyStudyHere: education?.currentlyStudyHere || false
        })
      );
      await dispatch(getEducations());
      await dispatch(getProfilePercentage());
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Education added successfully',
        duration: 3000
      }));
      await setModalConfig({
        section: '',
        item: null,
        mode: null
      });
    } catch (error) {
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while adding education',
        duration: 3000
      }));
    }
  };

  const updateEducation = async (education) => {
    try {
    const start = new Date(education.startdate);
    start.setHours(0);
    const end = new Date(education.enddate);
    end.setHours(0);

    await dispatch(
      updateEducationsItem({
        Id: education.id,
        UniversityName: education.school,
        Degree: education.degree,
        DegreeType: education.degreetype,
        FieldOfStudy: education.fieldofstudy,
        City: 'null',
        State: 'null',
        Country: 'null',
        StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        CurrentlyStudyHere: education?.currentlyStudyHere || false
      })
    );
    await dispatch(getEducations());
    await dispatch(getProfilePercentage());

    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Education updated successfully',
      duration: 3000
    }));
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
    } catch (error) {
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while updating education',
        duration: 3000
      }));
    }
  };

  const addTraining = async (training) => {
    try {
      await dispatch(
        addTrainingsItem({
          CertificationType: training.type,
          CertificationId: training.certId,
          ValidUntil: training.valid ? dateFormat(training.valid, 'isoDate') : dayjs(),
          StartDate: null,
          EndDate: null
        })
      );
      await dispatch(getTrainings());
      await dispatch(getProfilePercentage());
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Training added successfully',
        duration: 3000
      }));
      await setModalConfig({
        section: '',
        item: null,
        mode: null
      });
    } catch (error) {
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while adding training',
        duration: 3000
      }));
    }
  };

  const updateTraining = async (training) => {
    try {
      await dispatch(
        updateTrainingsItem({
          Id: training.id,
          CertificationType: training.type,
          CertificationId: training.certId,
          ValidUntil: training.valid ? dateFormat(training.valid, 'isoDate') : null,
          StartDate: null,
          EndDate: null
        })
      );
      await dispatch(getTrainings());
      await dispatch(getProfilePercentage());
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Training updated successfully',
        duration: 3000
      }));
      await setModalConfig({
        section: '',
        item: null,
        mode: null
      });
    } catch (error) {
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: Object.values(error.response?.data?.errors).flat().at(0) || 'An error occurred while updating training',
        duration: 3000
      }));
    }
  };

  const updateSkills = async (data) => {
    await dispatch(updateSkillsItems(userId, data.skills));
    await dispatch(getProfilePercentage());
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Skills updated successfully',
      duration: 3000
    }));
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
  };

  const deleteEducation = async (id) => {
    await dispatch(deleteEducationsItem(id));
    await dispatch(getProfilePercentage());
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Education deleted successfully',
      duration: 3000
    }));
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
  };

  const deleteWorkHistory = async (id) => {
    await dispatch(deleteWorkItem(id));
    await dispatch(deleteExistingWork(id));
    await dispatch(getProfilePercentage());
    await setDeleteWorkExperience('');
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Work experience deleted successfully',
      duration: 3000
    }));
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
  };

  const deleteTraining = async (id) => {
    await dispatch(deleteTrainingsItem(id));
    await dispatch(getProfilePercentage());
    await setModalConfig({
      section: '',
      item: null,
      mode: null
    });
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Training deleted successfully',
      duration: 3000
    }));
  };

  useEffect(() => {
    if (updatingError) {
      dispatch(setNotification({
          type: appNotificationsTypes.ERROR,
          message: updatingError,
          duration: 3000
      }))
    }
  }, [updatingError]);

  useEffect(() => {
    dispatch(getEducations());
    dispatch(getSkills(userId));
    dispatch(getTrainings());
  }, []);

  const updateCasuallySeeking = async () => {
    await dispatch(switchJobSeeking(profile?.Id, !profile?.IsJobSeeking));
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Job Seeking Status Updated',
      duration: 3000
    }));
  }

  const confirmWorkLocation = async () => {
    await dispatch(saveJobLocation(userId, { ...location }, location));
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Work Location Type Updated',
      duration: 3000
    }));
    await setEditWorkLocation(false);
  };

  const workLocation = [
    {
      id: 0,
      label: t('dashboard.profile.details.openremote'),
      option: 'OpentoRemoteWork',
      checked: profile?.OpentoRemoteWork
    },
    {
      id: 1,
      label: t('dashboard.profile.details.openrelocation'),
      option: 'OpenToRelocation',
      checked: profile?.OpenToRelocation
    }
  ];

const industriesForm = useFormik({
  initialValues: getInitialValues(true, profile?.UserIndustries, 'industries', t),
  validationSchema: getValidationSchema('industries', profile?.UserIndustries, t),
  initialTouched: Object.keys(getInitialValues(true, profile?.UserIndustries, 'industries', t)).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {}),
  onSubmit: (values) => console.log('Submit industries', values),
  enableReinitialize: true
});

const functionsForm = useFormik({
  initialValues: getInitialValues(true, profile?.UserJobFunctions, 'functions', t),
  validationSchema: getValidationSchema('functions', profile?.UserJobFunctions, t),
  initialTouched: Object.keys(getInitialValues(true, profile?.UserJobFunctions, 'functions', t)).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {}),
  onSubmit: (values) => console.log('Submit functions', values),
  enableReinitialize: true
});

  const saveJobPreferences = async () => {
    await setIsJobPreferencesSaving(true);
    await dispatch(
      setIndustries(
        profile.Id,
        industriesForm.values.industries.map((item) => ({ IndustryId: item.IndustryId })),
        industriesForm.values.industries
      )
    );
    await dispatch(
      setFunctions(
        profile.Id,
        functionsForm.values.functions.map((item) => ({ JobFunctionId: item.JobFunctionId })),
        functionsForm.values.functions
      )
    );
    if (Object.values(contractType).filter((item) => item).length) {
      await dispatch(saveContractType(userId, { ...contractType }, contractType));
    }

    await dispatch(saveUserRates(userId, { ...userRates, ...userRatesLocaly }));
    await setEditJobPreferences(false);
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Job Preferences successfully updated',
      duration: 3000
    }));
    await setIsJobPreferencesSaving(false);
  }

  const handleUpdateConsent = async () => {
    await dispatch(manageConsent(userId, consent));
    await setEditConsent(false);
    await dispatch(setNotification({
      type: appNotificationsTypes.SUCCESS,
      message: 'Privacy Preference successfully updated',
      duration: 3000
    }));
  }

  const updateConsent = (data) => {
    setConsent(data);
  }

  const handleCVSelection = (cv) => {
    setSelectedCV(cv);
  }

  const handleCVExtraction = async () => {
    const data = new FormData();
    data.append('cv', selectedCV[0]);
    await dispatch(cvUpload(data));
    await dispatch(cvParse(data));
  };

  const handleCvUploadInMobile = async (files) => {
    if (isMobile) {
      const data = new FormData();
      data.append('cv', files[0]);
      await dispatch(cvUpload(data));
      await dispatch(cvParse(data));
    }
  }

  useEffect(() => {
    if (pendingParse || pendingUpload) {
      setIsCVExtracting(true);
    }
  }, [pendingParse, pendingUpload]);

  const handleCompleteProfile = async () => {
    const constructedCv = cvParser(parsedCV);

    const userId = localStorage.getItem('UserId');

    try {
      await dispatch(removeNotification());
      await api.put(`v1/user/${userId}`, constructedCv);
      await dispatch(fetchUserData(userId));
      await dispatch(getEducations());
      await dispatch(getSkills(userId));
      await dispatch(getTrainings());
      await setIsCVExtracting(false);
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Your profile has been successfully created.',
        duration: 3000
      }));
      await dispatch(getProfilePercentage());
    } catch (e) {
      setIsCVExtracting(false);
      await dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: 'Error occurred while creating your profile, Please try again later.',
        duration: 3000
      }));
      throw e;
    } finally {
      dispatch(acknowledgeCvParse());
    }
  };

  useEffect(() => {
    if (isCvParsed) {
      handleCompleteProfile();
    }
  }, [isCvParsed]);

  const assesStrength = (data, dataCv, jobs, user, percentage) => [
    {
      label: t('dashboard.profile.uploadCv'),
      isSectionComplete: percentage?.UploadCV > 0,
      percentage: percentage?.UploadCV || 30,
      action: () => !user?.IsProfileCompleted && setIsProfileCompleted(false)
    },
    {
      label: t('dashboard.profile.personalDetails'),
      isSectionComplete: percentage?.PersonalDetails > 0,
      percentage: percentage?.PersonalDetails || 10,
      action: () => setEditPersonalDetails(true)
    },
    {
      label: t('dashboard.profile.jobPreferences'),
      isSectionComplete: percentage?.JobPreference > 0,
      percentage: percentage?.JobPreference || 10,
      action: () => setEditJobPreferences(true)
    },
    {
      label: t('dashboard.profile.about'),
      isSectionComplete: percentage?.Summary > 0,
      percentage: percentage?.Summary || 5,
      action: () => setEditSummary(true)
    },
    {
      label: t('dashboard.profile.workExperience'),
      isSectionComplete: percentage?.WorkExperiences > 0,
      percentage: percentage?.WorkExperiences || 10,
      action: () => setModalConfig({
        section: 'works',
        item: null,
        mode: 'add'
      })
    },
    {
      label: t('dashboard.profile.education'),
      isSectionComplete: percentage?.Educations > 0,
      percentage: percentage?.Educations || 10,
      action: () => setModalConfig({
        section: 'educations',
        item: null,
        mode: 'add'
      })
    },
    {
      label: t('dashboard.profile.certificates'),
      isSectionComplete: percentage?.Certificates > 0,
      percentage: percentage?.Certificates || 10,
      action: () => setModalConfig({
        section: 'trainings',
        item: null,
        mode: 'add'
      })
    },
    {
      label: t('dashboard.profile.skills'),
      isSectionComplete: percentage?.Skills > 0,
      percentage: percentage?.Skills || 5,
      action: () => setModalConfig({
        section: 'skills',
        item: null,
        mode: 'add'
      })
    },
    {
      label: t('dashboard.profile.applyJob'),
      isSectionComplete: percentage?.IsJobApplied > 0,
      percentage: percentage?.IsJobApplied || 10,
      action: () => navigate('/dashboard/jobs')
    }
  ];

  const handleUpdateSummary = async () => {
    try {
      await dispatch(updateProfileSummary(summary));
      setEditSummary(false);
      await dispatch(setNotification({
        type: appNotificationsTypes.SUCCESS,
        message: 'Summary updated successfully',
        duration: 3000
      }));
      await dispatch(getProfilePercentage());
    } catch (error) {
      dispatch(setNotification({
        type: appNotificationsTypes.ERROR,
        message: 'Error updating summary',
        duration: 3000
      }));
      throw error;
    }
  };

  useEffect(() => {
    const strength = assesStrength(profile, cv, appliedJobs, userData, percentages);
    setAssessedProfile(strength)
  }, [percentages]);

  useEffect(() => {
    if (locationRoute.pathname === '/my-profile') {
      setShowPercentageMobile(true);
    }
  }, [locationRoute]);

  useEffect(() => {
    console.log('Parsed CV:', parsedCV);
    const summary = parsedCV?.Summary || profile?.Summary || '';
    setSummary(summary);
  }, []);

  return (
    <section className={classes.container}>
      {profilePending || !profile || isCVExtracting ? (
        <>
          <MyProfileSkeleton />
          {isCVExtracting && (
            <BuildingProgress />
          )}
        </>
      ) : (
        <div className='flex mx-auto max-w-[1180px] px-0 md:px-8 gap-2'>
          <div className="flex flex-col gap-2">
            <ProfileHeader
              isJobPreferenceComplete={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.jobPreferences'))?.isSectionComplete}
              userName={`${profile?.FirstName} ${profile?.LastName}`}
              jobTitle={currentWork?.Title}
              location={[profile?.City, profile?.State, profile?.Country].filter(Boolean).join(', ')}
              email={profile?.PrimaryEmail}
              phone={`${profile?.PhoneCountryCode} ${profile?.Phone}`}
              contactType={profile?.PhoneType}
              isJobSeeking={profile?.IsJobSeeking}
              isJobSeekingPending={profile?.IsJobSeekingPending}
              onOpenToWork={() => {
                setLocation({
                  OpentoRemoteWork: profile?.OpentoRemoteWork,
                  OpenToRelocation: profile?.OpenToRelocation
                });
                setEditWorkLocation(true)
              }}
              onCasuallySeeking={() => updateCasuallySeeking()}
              handleEditPersonalDetails={() => setEditPersonalDetails(true)}
              handleJobPreferences={() => {
                setContractType({
                  ContractWork: profile?.ContractWork,
                  PermanentWork: profile?.PermanentWork
                });
                setEditJobPreferences(true)
              }}
              handleConsent={() => {
                setEditConsent(true);
              }}
            />
            <EditPersonalDetails
              data={profile}
              state={editPersonalDetails}
              changeState={setEditPersonalDetails}
              updateUserData={updatePersonalData}
              updating={updatingUserPending}
            />
            {console.log('userData: ', parsedCV)}
            <AboutComponent
              handleUpdateSummary={handleUpdateSummary}
              editSummary={editSummary}
              description={summary}
              summaryServerVersion={profile?.Summary || parsedCV?.Summary || ''}
              setDescription={setSummary}
              setEditSummary={setEditSummary}
              onEdit={() => setEditSummary(true)}
              profile={profile}
              isCompleted={userData?.IsProfileCompleted || userData?.CvName}
              setIsCompleted={setIsProfileCompleted}
              shouldShowNudge={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.about'))?.isSectionComplete}
            />
            <ResumeSection
              shouldShowNudge={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.workExperience'))?.isSectionComplete}
              sectionTitle={t('dashboard.profile.workexperience')}
              data={profile?.WorkHistories}
              onAdd={() => setModalConfig({
                section: 'works',
                item: null,
                mode: 'add'
              })}
              onEdit={(item) => setModalConfig({
                section: 'works',
                item,
                mode: 'edit'
              })}
              onDelete={(item) => setModalConfig({
                section: 'works',
                item,
                mode: 'delete'
              })}
              hideInlineIcon
              placeholder="You can describe your professional background, roles,
                and achievements here. Include job titles, company names, dates of employment,
                and key responsibilities or accomplishments."
              renderItem={(exp) => (
                <>
                  <div className='flex flex-row items-center gap-2'>
                    <div className='flex flex-col'>
                      <div className='font-medium'>{exp?.Title || 'No Title'}</div>
                      <div className='text-gray-500'>{exp?.CompanyName || 'No Company'}</div>
                      <div className='text-gray-500'>
                        {dayjs(exp?.StartDate).format(generalEnums.DATEWITHSTANDARD)} - {dayjs(exp?.EndDate).format(generalEnums.DATEWITHSTANDARD)}
                      </div>
                    </div>
                    <div className='flex md:hidden justify-between h-full flex-col ml-auto gap-3'>
                      <ModeOutlined
                        className="!text-xl md:!text-2xl"
                        sx={{ color: colors.NAVY_BLUE, cursor: 'pointer' }}
                        onClick={() => setModalConfig({
                          section: 'works',
                          item: exp,
                          mode: 'edit'
                        })}
                      />
                      <DeleteOutlined
                        className="!text-xl md:!text-2xl"
                        sx={{ color: colors.NAVY_BLUE, cursor: 'pointer' }}
                        onClick={() => setModalConfig({
                          section: 'works',
                          item: exp,
                          mode: 'delete'
                        })}
                      />
                    </div>
                  </div>
                  <div className='pt-2 flex flex-grow whitespace-pre-wrap'>{exp?.Description || ''}</div>
                </>
              )}
            />
            <ResumeSection
              shouldShowNudge={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.education'))?.isSectionComplete}
              sectionTitle={t('dashboard.profile.education')}
              data={cv?.educations?.response}
              placeholder="Add your academic background here, including degrees earned,
                institutions attended, graduation dates, and any honors or distinctions."
              icon={SchoolOutlined}
              onAdd={() => setModalConfig({
                section: 'educations',
                item: null,
                mode: 'add'
              })}
              onEdit={(item) => setModalConfig({
                section: 'educations',
                item,
                mode: 'edit'
              })}
              onDelete={(item) => setModalConfig({
                section: 'educations',
                item,
                mode: 'delete'
              })}
              renderItem={(edu) => (
                <>
                  <div className='font-medium'>{edu?.Degree || 'No Degree'}</div>
                  <div className='text-gray-500'>{edu?.UniversityName}</div>
                  <div className='text-gray-500'>
                    {dayjs(edu?.StartDate).format(generalEnums.MONTHANDYEAR)} - {dayjs(edu?.EndDate).format(generalEnums.MONTHANDYEAR)}
                  </div>
                  <div className='pt-2'>{edu?.FieldOfStudy}</div>
                </>
              )}
            />
            <ResumeSection
              shouldShowNudge={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.certificates'))?.isSectionComplete}
              sectionTitle={t('dashboard.profile.trainingcertificates')}
              data={cv?.trainings?.response}
              placeholder="List any relevant training programs or certifications you've completed.
                Include the name of the course, issuing organization, and completion date."
              icon={NewspaperOutlined}
              onAdd={() => setModalConfig({
                section: 'trainings',
                item: null,
                mode: 'add'
              })}
              onEdit={(item) => setModalConfig({
                section: 'trainings',
                item,
                mode: 'edit'
              })}
              onDelete={(item) => setModalConfig({
                section: 'trainings',
                item,
                mode: 'delete'
              })}
              renderItem={(cert) => (
                <>
                  <div className='font-medium'>
                    {cert?.CertificationType || 'No Certification Type'}
                  </div>
                  <div className='text-gray-500'>
                    {cert?.CertificationId}
                  </div>
                  <div className='text-gray-500'>
                    {dayjs(cert?.ValidUntil).format(generalEnums.DATEWITHSTANDARD) || 'N/A'}
                  </div>
                </>
              )}
            />
            <ResumeSection
              shouldShowNudge={!assessedProfile.find((prof) => prof.label === t('dashboard.profile.skills'))?.isSectionComplete}
              sectionTitle={t('dashboard.profile.skills')}
              placeholder="Highlight your key professional skills here.
                Include both technical and soft skills that are relevant to your role or industry."
              data={cv?.skills?.response || []}
              pillsType
              onEdit={(item) => setModalConfig({
                section: 'skills',
                item,
                mode: 'edit'
              })}
              renderItem={(skill) => (
                <>
                  <div className='font-medium'>
                    {skill?.SkillName}
                  </div>
                </>
              )}
            />

            <AddEditFormModal
              profile={profile}
              open={modalConfig.mode === 'add' || modalConfig.mode === 'edit'}
              closeHandler={() => setModalConfig({ section: '', item: null, mode: null })}
              editeMode={modalConfig.mode === 'edit'}
              initData={modalConfig.item || {}}
              confirmHandler={
                modalConfig.section === 'works'
                  ? modalConfig.mode === 'edit' ? updateWork : addWork
                  : modalConfig.section === 'educations'
                    ? modalConfig.mode === 'edit' ? updateEducation : addEducation
                    : modalConfig.section === 'trainings'
                      ? modalConfig.mode === 'edit' ? updateTraining : addTraining
                      : modalConfig.mode === 'edit' && updateSkills
              }
              block={modalConfig.section}
              pending={updatingPending || updateSkillsPending}
              withCurrentlyCheckBox
            />
            <DeleteDialog
              open={modalConfig.mode === 'delete'}
              closeHandler={() => setModalConfig({ section: '', item: null, mode: null })}
              confirmHandler={() => {
                const id = modalConfig.item?.Id || modalConfig.item?.id;
                if (!id) return;

                const deleteMap = {
                  works: deleteWorkHistory,
                  educations: deleteEducation,
                  trainings: deleteTraining,
                };

                deleteMap[modalConfig.section](id);
              }}
              config={deleteModalConfig[modalConfig.section]}
              pending={updatingPending}
            />
            <BaseModal
              title={t('dashboard.modal.edit.worklocation')}
              open={editWorkLocation}
              closeHandler={() => {
                setLocation({
                  OpentoRemoteWork: profile?.OpentoRemoteWork,
                  OpenToRelocation: profile?.OpenToRelocation
                });
                setEditWorkLocation(false);
              }}
              confirmHandler={confirmWorkLocation}
              pending={updatingPending}
            >
              <PreferenceTypeThree
                location={location}
                setLocation={setLocation}
                fields={workLocation}
                sx={{
                  fontSize: '14px'
                }}
                labelSx='!text-sm'
              />
            </BaseModal>
            <BaseModal
              title={t('dashboard.modal.edit.jobpreferences')}
              open={editJobPreferences}
              closeHandler={() => {
                setEditJobPreferences(false);
              }}
              confirmHandler={saveJobPreferences}
              pending={isJobPreferencesSaving}
            >
              <JobPreferences
                updateRates={setUserRatesLocaly}
                validationErrors={validationErrors}
                profile={profile}
                jobLocation={location}
                updating={updatingPending}
                industriesForm={industriesForm}
                functionsForm={functionsForm}
                contractType={contractType}
                setContractType={setContractType}
              />
            </BaseModal>
            <BaseModal
              title={t('dashboard.modal.edit.consent')}
              open={editConsent}
              closeHandler={() => {
                setEditConsent(false);
              }}
              confirmHandler={handleUpdateConsent}
              pending={profile?.consentPending}
            >
              <ConsentForm
                consent={consent}
                setConsent={setConsent}
                revoke={updateConsent}
              />
            </BaseModal>
            <ProfileBuilder
              isOpen={!isProfileCompleted && !userData?.CvName}
              onFilesSelected={handleCVSelection}
              onUploadCvMobile={handleCvUploadInMobile}
              onSkip={() => setIsProfileCompleted(true)}
              onExtract={handleCVExtraction}
            />
          </div>
          <div className='hidden md:flex flex-col min-w-[270px] sticky top-0'>
            <ProfileStrength strength={assessedProfile} total={percentages?.ProfileCompletionPercentage} />
          </div>
          {isMobile && (
          <div className='flex md:hidden'>
            <BaseSwipeableDrawer open={showPercentageMobile} closeHandler={() => setShowPercentageMobile(false)}>
              <div className='w-full'>
                <ProfileStrength customClass="!bg-[#f3f4f7]" strength={assessedProfile} />
              </div>
            </BaseSwipeableDrawer>
          </div>
          )}
        </div>
      )}
    </section>
  );
}

export default MyProfile;
